import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowDown, ArrowUp, Trash2 } from 'lucide-react';
import { useDeletePackage, usePackage, useSavePackage, useTemplateList } from './hooks';
import { usePageTitle } from '../../hooks/usePageTitle';

interface DraftItem {
  templateId: string;
  templateName: string;
  isRequired: boolean;
}

export function PackageEditor() {
  usePageTitle('Package');
  const { id } = useParams();
  const isNew = id === 'new' || !id;
  const effectiveId = isNew ? undefined : id;
  const nav = useNavigate();
  const { data: existing } = usePackage(effectiveId);
  const { data: templates } = useTemplateList(false);
  const save = useSavePackage();
  const del = useDeletePackage();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [items, setItems] = useState<DraftItem[]>([]);

  useEffect(() => {
    if (existing) {
      setName(existing.name);
      setDescription(existing.description ?? '');
      setItems(
        existing.items.map((i) => ({
          templateId: i.templateId,
          templateName: i.templateName,
          isRequired: i.isRequired
        }))
      );
    }
  }, [existing]);

  const canSubmit = name.trim() !== '' && items.length > 0;

  const moveUp = (idx: number) => {
    if (idx === 0) return;
    setItems((s) => {
      const copy = s.slice();
      [copy[idx - 1], copy[idx]] = [copy[idx], copy[idx - 1]];
      return copy;
    });
  };

  const moveDown = (idx: number) => {
    setItems((s) => {
      if (idx === s.length - 1) return s;
      const copy = s.slice();
      [copy[idx], copy[idx + 1]] = [copy[idx + 1], copy[idx]];
      return copy;
    });
  };

  const addTemplate = (templateId: string) => {
    const t = templates?.find((x) => x.id === templateId);
    if (!t) return;
    if (items.some((i) => i.templateId === templateId)) return;
    setItems((s) => [...s, { templateId: t.id, templateName: t.name, isRequired: true }]);
  };

  const remove = (idx: number) => setItems((s) => s.filter((_, i) => i !== idx));

  const onSubmit = async () => {
    await save.mutateAsync({
      id: effectiveId,
      name: name.trim(),
      description: description.trim() || undefined,
      items: items.map((i, idx) => ({
        templateId: i.templateId,
        sortOrder: idx,
        isRequired: i.isRequired
      }))
    });
    nav('/documents/packages');
  };

  const onDelete = async () => {
    if (!effectiveId) return;
    if (!confirm(`Delete bundle "${name}"? This can't be undone. Documents already sent are unaffected.`)) return;
    await del.mutateAsync(effectiveId);
    nav('/documents/packages');
  };

  const selectableTemplates = (templates ?? []).filter(
    (t) => !items.some((i) => i.templateId === t.id)
  );

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      <div>
        <Link to="/documents/packages" className="text-sm text-slate-500 hover:text-sky-600">
          ← Bundles
        </Link>
        <h1 className="text-2xl font-semibold text-slate-800 mt-2">
          {isNew ? 'New bundle' : 'Edit bundle'}
        </h1>
      </div>

      <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
        <label className="block">
          <span className="block text-sm font-medium text-slate-700 mb-1">Name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full border border-slate-300 rounded px-3 py-2 min-h-[44px]"
            placeholder="e.g. Standard Onboarding"
          />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-slate-700 mb-1">Description</span>
          <textarea
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full border border-slate-300 rounded px-3 py-2"
          />
        </label>

        <div>
          <h2 className="text-sm font-medium text-slate-700 mb-2">Items (signed in order)</h2>
          {items.length === 0 ? (
            <p className="text-sm text-slate-500 italic">No items yet — add one below.</p>
          ) : (
            <ol className="space-y-2">
              {items.map((item, idx) => (
                <li
                  key={item.templateId}
                  className="flex items-center gap-2 border border-slate-200 rounded px-3 py-2"
                >
                  <div className="flex flex-col">
                    <button
                      type="button"
                      onClick={() => moveUp(idx)}
                      disabled={idx === 0}
                      className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
                      aria-label="Move up"
                    >
                      <ArrowUp size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveDown(idx)}
                      disabled={idx === items.length - 1}
                      className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
                      aria-label="Move down"
                    >
                      <ArrowDown size={14} />
                    </button>
                  </div>
                  <span className="flex-1 font-medium text-slate-800">
                    {idx + 1}. {item.templateName}
                  </span>
                  <label className="flex items-center gap-1 text-xs text-slate-600">
                    <input
                      type="checkbox"
                      checked={item.isRequired}
                      onChange={(e) =>
                        setItems((s) => s.map((x, i) => (i === idx ? { ...x, isRequired: e.target.checked } : x)))
                      }
                    />
                    Required
                  </label>
                  <button
                    type="button"
                    onClick={() => remove(idx)}
                    className="text-slate-400 hover:text-red-600"
                    aria-label="Remove"
                  >
                    <Trash2 size={14} />
                  </button>
                </li>
              ))}
            </ol>
          )}

          {selectableTemplates.length > 0 && (
            <div className="flex items-center gap-2 pt-3">
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    addTemplate(e.target.value);
                    e.target.value = '';
                  }
                }}
                className="border border-slate-300 rounded px-3 py-2 min-h-[44px] text-sm"
                defaultValue=""
              >
                <option value="" disabled>
                  + Add template…
                </option>
                {selectableTemplates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit || save.isPending}
            className="bg-sky-600 text-white px-4 py-2 rounded hover:bg-sky-700 disabled:opacity-50 text-sm"
          >
            {save.isPending ? 'Saving…' : isNew ? 'Create package' : 'Save package'}
          </button>
          <Link to="/documents/packages" className="text-slate-600 hover:text-sky-600 text-sm">Cancel</Link>
          {!isNew && (
            <button
              type="button"
              onClick={onDelete}
              disabled={del.isPending}
              className="ml-auto text-red-600 hover:text-red-700 text-sm inline-flex items-center gap-1 disabled:opacity-50"
            >
              <Trash2 size={14} /> {del.isPending ? 'Deleting…' : 'Delete bundle'}
            </button>
          )}
        </div>
      </section>
    </div>
  );
}
